import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { isUserDeactivatedState, isUserLoadingState, userState } from '../state/UIState';
import { useOktaAuth } from '@okta/okta-react';
import { fetchUserByEmail, useCreateUser } from '../queries/hooks/useUsers';
import axios from 'axios';
import { extractErrorMessage, notifyError } from '../utils/errorNotifier';

const useInitUser = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const setIsLoadingUser = useSetAtom(isUserLoadingState);
  const setUserState = useSetAtom(userState);
  const setIsUserDeactivated = useSetAtom(isUserDeactivatedState);
  const createUser = useCreateUser();

  const login = async () => oktaAuth.signInWithRedirect();

  useEffect(() => {
    if (!authState?.idToken?.claims?.email) return;

    const initUser = async () => {
      try {
        const email = authState?.idToken?.claims?.email?.toLowerCase();
        const user = await fetchUserByEmail((email as string) ?? '');
        if (user) {
          if (user.isActive === false) {
            setIsUserDeactivated(true);
            return;
          }
          setUserState(user);
        } else {
          const newUser = await createUser.mutateAsync({
            name: authState?.idToken?.claims?.name ?? '',
            email: email ?? '',
          });
          setUserState(newUser);
        }
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 403 &&
          error.response?.data?.message?.toLowerCase().includes('deactivated')
        ) {
          setIsUserDeactivated(true);
          return;
        }
        notifyError(extractErrorMessage(error));
      } finally {
        setIsLoadingUser(false);
      }
    };

    initUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.idToken?.claims?.email]);

  if (!authState) {
    return null;
  }

  if (!authState.isAuthenticated) {
    login();
  }
};

export default useInitUser;
